import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Briefcase, Users, FileText, TrendingUp, LogOut, Search, MessageSquare, Building2, Settings, AlertCircle, CheckCircle, Filter, Copy, Shield} from 'lucide-react'
import DashboardSwitcher from "../components/DashboardSwitcher"
import { isSuperadmin } from "../config/roles"
import { checkRecruiterAccess } from "../lib/mouAccessControl"

type Tab = "overview" | "search" | "messages" | "profile" | "settings"

interface RecruiterProfile {
  company_name?: string
  mou_signed?: boolean
  contact_name?: string
  email?: string
  phone?: string
  region?: string
}

interface NurseProfile {
  _id: string
  specialty?: string[]
  years_experience?: number
  nclex_status?: string
  cgfns_status?: string
  dataflow_status?: string
  country?: string
  anonymized_id?: string
}

// Fixed: Dashboard loading issue - force rebuild
export default function RecruiterDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>("overview")
  const [profile, setProfile] = useState<RecruiterProfile | null>(null)
  const [nurses, setNurses] = useState<NurseProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [mouAccessCheck, setMouAccessCheck] = useState<any>(null)
  const [searchFilters, setSearchFilters] = useState({
    specialty: "",
    experience_min: "",
    nclex_passed: false,
    cgfns_passed: false,
    dataflow_passed: false
  })

  useEffect(() => {
    loadProfile()
    checkMOUStatus()
    if (activeTab === "search") {
      loadNurses()
    }
    if (activeTab === "messages") {
      navigate("/dashboard/recruiter/messages")
    }
  }, [activeTab, navigate])

  const checkMOUStatus = async () => {
    const userId = lumi.auth.user?.userId
    if (!userId) return

    const accessResult = await checkRecruiterAccess(userId)
    setMouAccessCheck(accessResult)
    
    if (accessResult.warning) {
      toast(accessResult.warning, { icon: "⚠️", duration: 5000 })
    }
  }

  const loadProfile = async () => {
    const userId = lumi.auth.user?.userId
    if (!userId) {
      setLoading(false)
      navigate("/auth")
      return
    }

    try {
      const result = await lumi.entities.UserProfiles.list({
        filter: { user_id: userId }
      })

      if (result.list.length > 0) {
        setProfile(result.list[0] as RecruiterProfile)
      }
    } catch (error) {
      console.error("Failed to load profile:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadNurses = async () => {
    try {
      // Check if user is superadmin (bypass MOU requirement)
      const userEmail = lumi.auth.user?.email || ""
      const canBypassMOU = isSuperadmin(userEmail)
      
      // Debug logging
      console.log("loadNurses - User email:", userEmail)
      console.log("loadNurses - User object:", lumi.auth.user)
      console.log("loadNurses - Can bypass MOU:", canBypassMOU)
      console.log("loadNurses - MOU signed:", profile?.mou_signed)
      
      // Only load if MOU is signed OR user is superadmin
      if (!profile?.mou_signed && !canBypassMOU) {
        console.log("loadNurses - BLOCKED: MOU not signed and not superadmin")
        return
      }
      console.log("loadNurses - ALLOWED: Proceeding to load nurses")

      const filter: any = { role: "nurse" }
      
      if (searchFilters.specialty) {
        filter.specialty = { $in: [searchFilters.specialty] }
      }
      if (searchFilters.experience_min) {
        filter.years_experience = { $gte: parseInt(searchFilters.experience_min) }
      }
      if (searchFilters.nclex_passed) {
        filter.nclex_status = "Passed"
      }
      if (searchFilters.cgfns_passed) {
        filter.cgfns_status = "Completed"
      }
      if (searchFilters.dataflow_passed) {
        filter.dataflow_status = "Completed"
      }

      const result = await lumi.entities.UserProfiles.list({ filter, limit: 50 })
      setNurses(result.list as NurseProfile[])
    } catch (error) {
      console.error("Failed to load nurses:", error)
      toast.error("Failed to load nurse profiles")
    }
  }

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  const handleSearch = () => {
    loadNurses()
  }

  const renderOverview = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome, {profile?.contact_name || lumi.auth.user?.userName}</h2>
        <p className="text-gray-600">
          Your recruiter dashboard gives you access to verified nurses once you complete your MOU.
        </p>
      </div>

      {mouAccessCheck && mouAccessCheck.accessLevel === "limited" && !isSuperadmin(lumi.auth.user?.email || "") && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <Shield className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">🔒 Limited Access Mode</h3>
              <p className="text-blue-800 text-sm mb-3">
                You're viewing <strong>limited nurse profiles</strong>. To access complete information (documents, certificates, contact details), please sign the Nursing Hive MOU.
              </p>
              <div className="bg-white rounded-lg p-3 mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">You can currently see:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-3 h-3" />
                    <span>Name</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-3 h-3" />
                    <span>Country</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-3 h-3" />
                    <span>Specialization</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-3 h-3" />
                    <span>Education Level</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <a 
                  href="mailto:hello@nursinghive.com?subject=MOU Request" 
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Request MOU Approval
                </a>
                <a 
                  href="/resources/mou" 
                  className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium border border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {mouAccessCheck && mouAccessCheck.accessLevel === "full" && mouAccessCheck.expiryDays !== null && mouAccessCheck.expiryDays <= 30 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-1">MOU Expiring Soon</h3>
            <p className="text-yellow-800 text-sm">
              Your MOU expires in {mouAccessCheck.expiryDays} days. Please renew to maintain full access.
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-teal-600" />
            <span className="text-2xl font-bold text-gray-900">{nurses.length}</span>
          </div>
          <p className="text-gray-600">Available Nurses</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Briefcase className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">0</span>
          </div>
          <p className="text-gray-600">Active Job Posts</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <FileText className="w-8 h-8 text-purple-600" />
            <span className="text-2xl font-bold text-gray-900">0</span>
          </div>
          <p className="text-gray-600">Applications</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">0%</span>
          </div>
          <p className="text-gray-600">Match Rate</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setActiveTab("search")}
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Search className="w-6 h-6 text-teal-600" />
            <div className="text-left">
              <div className="font-semibold text-gray-900">Search Nurses</div>
              <div className="text-sm text-gray-600">
                {mouAccessCheck?.accessLevel === "limited" ? "View limited profiles" : "Find verified candidates"}
              </div>
            </div>
          </button>
          <button
            onClick={() => setActiveTab("messages")}
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <MessageSquare className="w-6 h-6 text-blue-600" />
            <div className="text-left">
              <div className="font-semibold text-gray-900">Messages</div>
              <div className="text-sm text-gray-600">Contact candidates</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  )

  const renderSearch = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Search Verified Nurses</h2>
        {mouAccessCheck && (
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100">
            <div className={`w-2 h-2 rounded-full ${mouAccessCheck.accessLevel === "full" ? "bg-green-500" : "bg-yellow-500"}`}></div>
            <span className="text-xs font-medium text-gray-700">
              {mouAccessCheck.accessLevel === "full" ? "Full Access" : "Limited Access"}
            </span>
          </div>
        )}
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Search Filters</h3>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
            <select
              value={searchFilters.specialty}
              onChange={(e) => setSearchFilters({ ...searchFilters, specialty: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">All Specialties</option>
              <option value="Med-Surg">Medical-Surgical (Med-Surg)</option>
              <option value="ICU">Intensive Care Unit (ICU)</option>
              <option value="CCU">Cardiac Care Unit (CCU)</option>
              <option value="NICU">Neonatal ICU (NICU)</option>
              <option value="PICU">Pediatric ICU (PICU)</option>
              <option value="ER/Trauma">Emergency Room / Trauma</option>
              <option value="OR">Operating Room (OR)</option>
              <option value="PACU">Post-Anesthesia Care (PACU)</option>
              <option value="Labor & Delivery">Labor & Delivery</option>
              <option value="OB/GYN">Obstetrics & Gynecology</option>
              <option value="Pediatrics">Pediatrics</option>
              <option value="Geriatrics">Geriatrics</option>
              <option value="Telemetry">Telemetry</option>
              <option value="Oncology">Oncology</option>
              <option value="Dialysis">Dialysis / Nephrology</option>
              <option value="Psychiatry">Psychiatry / Mental Health</option>
              <option value="Rehabilitation">Rehabilitation</option>
              <option value="Home Health">Home Health</option>
              <option value="Long-term Care">Long-term Care / Nursing Home</option>
              <option value="Infection Control">Infection Control</option>
              <option value="Case Management">Case Management</option>
              <option value="Public Health">Public Health / Community</option>
              <option value="Ambulatory Care">Ambulatory Care / Outpatient</option>
              <option value="Hospice/Palliative">Hospice / Palliative Care</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Experience (Years)</label>
            <input
              type="number"
              value={searchFilters.experience_min}
              onChange={(e) => setSearchFilters({ ...searchFilters, experience_min: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="0"
              min="0"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={searchFilters.nclex_passed}
              onChange={(e) => setSearchFilters({ ...searchFilters, nclex_passed: e.target.checked })}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">NCLEX Passed</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={searchFilters.cgfns_passed}
              onChange={(e) => setSearchFilters({ ...searchFilters, cgfns_passed: e.target.checked })}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">CGFNS Completed</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={searchFilters.dataflow_passed}
              onChange={(e) => setSearchFilters({ ...searchFilters, dataflow_passed: e.target.checked })}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">Dataflow Completed</span>
          </label>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Specialty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badges</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {nurses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No nurses found matching your criteria
                  </td>
                </tr>
              ) : (
                nurses.map((nurse) => (
                  <tr key={nurse._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {nurse.anonymized_id || nurse._id.slice(-8)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {nurse.specialty?.join(", ") || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {nurse.years_experience || 0} years
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-1">
                        {nurse.nclex_status === "Passed" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            NCLEX
                          </span>
                        )}
                        {nurse.cgfns_status === "Completed" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            CGFNS
                          </span>
                        )}
                        {nurse.dataflow_status === "Completed" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Dataflow
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {nurse.country || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button className="text-teal-600 hover:text-teal-800 font-medium">
                        View Profile
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  // Messages tab navigates to /messages page via useEffect - no render function needed

  const renderProfile = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Profile</h2>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <p className="text-gray-900">{profile?.company_name || "Not provided"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
            <p className="text-gray-900">{profile?.contact_name || "Not provided"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{profile?.email || lumi.auth.user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <p className="text-gray-900">{profile?.phone || "Not provided"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
            <p className="text-gray-900">{profile?.region || "Not provided"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MOU Status</label>
            <div className="flex items-center gap-2">
              {profile?.mou_signed ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-600 font-medium">Signed</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-600 font-medium">Pending</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderSettings = () => (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings</h2>
      <p className="text-gray-600 mb-6">
        Account and notification settings
      </p>
      <div className="inline-block px-4 py-2 bg-gray-100 text-gray-800 rounded-lg font-medium">
        Coming Soon
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://i.imghippo.com/files/KBd1355nw.png"
              alt="Nursing Hive"
              className="w-10 h-auto"
            />
            <h1 className="text-xl font-bold text-gray-900">Recruiter Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm p-4 space-y-1">
              <button
                onClick={() => setActiveTab("overview")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "overview" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Briefcase className="w-5 h-5" />
                <span className="font-medium">Overview</span>
              </button>
              <button
                onClick={() => setActiveTab("search")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "search" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Search className="w-5 h-5" />
                <span className="font-medium">Search Nurses</span>
              </button>
              <button
                onClick={() => setActiveTab("messages")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "messages" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                <span className="font-medium">Messages</span>
              </button>
              <button
                onClick={() => setActiveTab("profile")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "profile" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span className="font-medium">Company Profile</span>
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "settings" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === "overview" && renderOverview()}
            {activeTab === "search" && renderSearch()}
            {activeTab === "profile" && renderProfile()}
            {activeTab === "settings" && renderSettings()}
          </div>
        </div>
      </div>
    </div>
  )
}
